import React from 'react';
import { Typography, Select, Space, Tooltip } from 'antd';
import {
    ArrowRightOutlined,
    ArrowDownOutlined,
    AlignLeftOutlined,
    AlignCenterOutlined,
    MenuOutlined
} from '@ant-design/icons';

const { Text } = Typography;

/**
 * AutoLayoutControl - Figma-like auto-layout controls for direction, alignment, gap
 */
const AutoLayoutControl = ({
    layout = {},
    spacingTokens = [],
    onChange
}) => {
    const {
        direction = 'column',
        align = 'stretch',
        justify = 'start',
        gap = 'space-2'
    } = layout;

    const handleChange = (key, value) => {
        onChange?.({
            ...layout,
            [key]: value
        });
    };

    // Direction options
    const directionOptions = [
        { value: 'row', icon: <ArrowRightOutlined />, label: 'Row' },
        { value: 'column', icon: <ArrowDownOutlined />, label: 'Column' }
    ];

    // Alignment options (cross-axis)
    const alignOptions = [
        { value: 'start', label: direction === 'row' ? 'Top' : 'Left' },
        { value: 'center', label: 'Center' },
        { value: 'end', label: direction === 'row' ? 'Bottom' : 'Right' },
        { value: 'stretch', label: 'Stretch' }
    ];

    // Justify options (main-axis)
    const justifyOptions = [
        { value: 'start', label: 'Start' },
        { value: 'center', label: 'Center' },
        { value: 'end', label: 'End' },
        { value: 'space-between', label: 'Space Between' },
        { value: 'space-around', label: 'Space Around' }
    ];

    // Gap options from spacing tokens
    const gapOptions = spacingTokens.map(token => ({
        value: token.name,
        label: `${token.name} (${token.mobile}px)`
    }));

    const ControlSection = ({ label, children }) => (
        <div style={{ marginBottom: 16 }}>
            <Text style={{
                color: '#8b949e',
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                display: 'block',
                marginBottom: 8
            }}>
                {label}
            </Text>
            {children}
        </div>
    );

    const ToggleButton = ({ selected, onClick, icon, label, tooltip }) => (
        <Tooltip title={tooltip || label}>
            <div
                onClick={onClick}
                style={{
                    flex: 1,
                    padding: '8px 12px',
                    background: selected ? '#58a6ff20' : '#161b22',
                    border: `1px solid ${selected ? '#58a6ff' : '#30363d'}`,
                    borderRadius: 6,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    transition: 'all 0.15s ease'
                }}
            >
                {icon && <span style={{ color: selected ? '#58a6ff' : '#8b949e' }}>{icon}</span>}
                <Text style={{
                    color: selected ? '#e6edf3' : '#8b949e',
                    fontSize: 12
                }}>
                    {label}
                </Text>
            </div>
        </Tooltip>
    );

    // Visual alignment grid for cross-axis
    const AlignmentGrid = () => {
        const isRow = direction === 'row';

        return (
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 4
            }}>
                {alignOptions.map(opt => (
                    <ToggleButton
                        key={opt.value}
                        selected={align === opt.value}
                        onClick={() => handleChange('align', opt.value)}
                        label={opt.label}
                        tooltip={`Align items: ${opt.label}`}
                    />
                ))}
            </div>
        );
    };

    return (
        <div style={{
            padding: 16,
            background: '#0d1117',
            borderRadius: 8,
            border: '1px solid #21262d'
        }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 16,
                paddingBottom: 12,
                borderBottom: '1px solid #21262d'
            }}>
                <MenuOutlined style={{ color: '#58a6ff', fontSize: 14 }} />
                <Text style={{ color: '#e6edf3', fontWeight: 500 }}>
                    Auto Layout
                </Text>
            </div>

            {/* Direction */}
            <ControlSection label="Direction">
                <div style={{ display: 'flex', gap: 8 }}>
                    {directionOptions.map(opt => (
                        <ToggleButton
                            key={opt.value}
                            selected={direction === opt.value}
                            onClick={() => handleChange('direction', opt.value)}
                            icon={opt.icon}
                            label={opt.label}
                        />
                    ))}
                </div>
            </ControlSection>

            {/* Alignment (Cross-axis) */}
            <ControlSection label={`Align Items (${direction === 'row' ? 'Vertical' : 'Horizontal'})`}>
                <AlignmentGrid />
            </ControlSection>

            {/* Justify (Main-axis) */}
            <ControlSection label={`Justify Content (${direction === 'row' ? 'Horizontal' : 'Vertical'})`}>
                <Select
                    value={justify}
                    onChange={(value) => handleChange('justify', value)}
                    options={justifyOptions}
                    style={{ width: '100%' }}
                    popupClassName="dark-dropdown"
                />
            </ControlSection>

            {/* Gap */}
            <ControlSection label="Gap">
                <Select
                    value={gap}
                    onChange={(value) => handleChange('gap', value)}
                    style={{ width: '100%' }}
                    popupClassName="dark-dropdown"
                    showSearch
                    optionFilterProp="label"
                >
                    <Select.OptGroup label="Spacing Tokens">
                        {gapOptions.map(opt => (
                            <Select.Option key={opt.value} value={opt.value} label={opt.label}>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>{opt.value}</span>
                                    <span style={{ color: '#8b949e' }}>{opt.label.split('(')[1]}</span>
                                </div>
                            </Select.Option>
                        ))}
                    </Select.OptGroup>
                    <Select.OptGroup label="Custom">
                        <Select.Option value="0" label="0px">0px</Select.Option>
                        <Select.Option value="4px" label="4px">4px</Select.Option>
                        <Select.Option value="8px" label="8px">8px</Select.Option>
                        <Select.Option value="12px" label="12px">12px</Select.Option>
                        <Select.Option value="16px" label="16px">16px</Select.Option>
                        <Select.Option value="24px" label="24px">24px</Select.Option>
                        <Select.Option value="32px" label="32px">32px</Select.Option>
                    </Select.OptGroup>
                </Select>
            </ControlSection>

            {/* Visual Preview */}
            <div style={{
                padding: 12,
                background: '#161b22',
                borderRadius: 6,
                border: '1px dashed #30363d'
            }}>
                <Text style={{
                    color: '#8b949e',
                    fontSize: 10,
                    textTransform: 'uppercase',
                    display: 'block',
                    marginBottom: 8
                }}>
                    Preview
                </Text>
                <div style={{
                    display: 'flex',
                    flexDirection: direction,
                    alignItems: align === 'stretch' ? 'stretch' : `flex-${align === 'start' ? 'start' : align === 'end' ? 'end' : 'center'}`,
                    justifyContent: justify === 'start' ? 'flex-start' : justify === 'end' ? 'flex-end' : justify,
                    gap: 8, // Simplified for preview
                    padding: 8,
                    background: '#0d1117',
                    borderRadius: 4,
                    minHeight: 60
                }}>
                    {[1, 2, 3].map(i => (
                        <div
                            key={i}
                            style={{
                                width: direction === 'row' ? 24 : (align === 'stretch' ? '100%' : 24),
                                height: direction === 'column' ? 16 : (align === 'stretch' ? '100%' : 16),
                                minHeight: 16,
                                minWidth: 24,
                                background: '#58a6ff',
                                borderRadius: 3,
                                opacity: 0.6 + (i * 0.1)
                            }}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default AutoLayoutControl;
